import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const TaxonomicNamingCalculator = () => {
  const [taxonData, setTaxonData] = useState({
    genus: '',
    species: '',
    subspecies: '',
    commonName: '',
    taxonomicRank: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTaxonData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const generateTaxonomicName = () => {
    const { genus, species, subspecies, commonName, taxonomicRank } = taxonData;
    
    if (!genus.trim() || !species.trim() || !taxonomicRank.trim()) {
      setError('Please enter all required fields');
      return;
    }

    // Generate taxonomic name components
    const taxonomicNameComponents = {
      scientificName: `${genus} ${species}${subspecies ? ` ${subspecies}` : ''}`,
      commonName: commonName || 'No common name provided',
      fullTaxonomicName: `${genus} ${species}${subspecies ? ` ${subspecies}` : ''} (${taxonomicRank})`,
      abbreviatedName: `${genus[0]}. ${species}`,
      taxonomicIdentity: `${genus} ${species} (${taxonomicRank})`
    };

    // Generate taxonomic rules
    const taxonomicRules = {
      genus: 'Genus name should be capitalized and italicized',
      species: 'Species name should be lowercase and italicized',
      subspecies: 'Subspecies name should be lowercase and italicized if present',
      commonName: 'Common name should be in plain text',
      rank: 'Taxonomic rank should be in plain text'
    };

    // Generate practical example usage
    const exampleUsage = {
      research: `Use ${taxonomicNameComponents.scientificName} in scientific publications.`,
      education: `Teach about ${taxonomicNameComponents.commonName} in biology classes.`,
      conservation: `List ${taxonomicNameComponents.fullTaxonomicName} in conservation documents.`,
      documentation: `Use ${taxonomicNameComponents.taxonomicIdentity} in field guides.`
    };

    // Generate detailed calculation steps
    const calculationSteps = [
      'Step 1: Analyze Taxonomic Components',
      `Genus: ${genus} (Capitalized and italicized)`,
      `Species: ${species} (Lowercase and italicized)`,
      `Subspecies: ${subspecies || 'None'} (Lowercase and italicized if present)`,
      `Common Name: ${commonName || 'None'} (Plain text)`,
      `Taxonomic Rank: ${taxonomicRank} (Plain text)`,
      '\nStep 2: Create Taxonomic Identity',
      `Scientific Name: ${taxonomicNameComponents.scientificName}`,
      `Common Name: ${taxonomicNameComponents.commonName}`,
      `Full Taxonomic Name: ${taxonomicNameComponents.fullTaxonomicName}`,
      `Abbreviated Name: ${taxonomicNameComponents.abbreviatedName}`,
      `Taxonomic Identity: ${taxonomicNameComponents.taxonomicIdentity}`,
      '\nStep 3: Apply Taxonomic Rules',
      `Genus: ${taxonomicRules.genus}`,
      `Species: ${taxonomicRules.species}`,
      `Subspecies: ${taxonomicRules.subspecies}`,
      `Common Name: ${taxonomicRules.commonName}`,
      `Rank: ${taxonomicRules.rank}`
    ];

    // Generate enhanced chart data
    const chartData = {
      bar: {
        labels: ['Genus', 'Species', 'Subspecies', 'Common Name', 'Rank'],
        datasets: [{
          label: 'Taxonomic Components',
          data: [1, 1, subspecies ? 1 : 0, commonName ? 1 : 0, 1],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            subspecies ? 'rgba(255, 99, 132, 0.5)' : 'rgba(0, 0, 0, 0)',
            commonName ? 'rgba(153, 102, 255, 0.5)' : 'rgba(0, 0, 0, 0)',
            'rgba(255, 159, 64, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            subspecies ? 'rgba(255, 99, 132, 1)' : 'rgba(0, 0, 0, 0)',
            commonName ? 'rgba(153, 102, 255, 1)' : 'rgba(0, 0, 0, 0)',
            'rgba(255, 159, 64, 1)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Genus', 'Species', 'Subspecies', 'Common Name', 'Rank'],
        datasets: [{
          data: [1, 1, subspecies ? 1 : 0, commonName ? 1 : 0, 1],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            subspecies ? 'rgba(255, 159, 64, 0.7)' : 'rgba(0, 0, 0, 0)',
            commonName ? 'rgba(153, 102, 255, 0.7)' : 'rgba(0, 0, 0, 0)',
            'rgba(75, 192, 192, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            subspecies ? 'rgba(255, 159, 64, 1)' : 'rgba(0, 0, 0, 0)',
            commonName ? 'rgba(153, 102, 255, 1)' : 'rgba(0, 0, 0, 0)',
            'rgba(75, 192, 192, 1)'
          ],
          borderWidth: 1
        }]
      }
    };

    const newResults = {
      taxonomicNameComponents,
      taxonomicRules,
      exampleUsage,
      calculationSteps
    };

    setResults(newResults);
    setChartData(chartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Taxonomic Naming Calculator | Generate Scientific Names</title>
        <meta name="description" content="Generate taxonomic names with our Taxonomic Naming Calculator. Get accurate naming conventions for species, genus, and subspecies." />
        <meta name="keywords" content="taxonomic naming, scientific naming, species naming, genus, subspecies, binomial nomenclature" />
        <meta property="og:title" content="Taxonomic Naming Calculator | Generate Scientific Names" />
        <meta property="og:description" content="Generate taxonomic names with our Taxonomic Naming Calculator. Get accurate naming conventions for species, genus, and subspecies." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/taxonomic-naming-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🐾</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Taxonomic Naming Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Generate Accurate Scientific Names</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator helps you generate taxonomic names using standardized naming conventions. It provides naming rules and examples for different taxonomic components.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="genus">
            Genus:
          </label>
          <input
            type="text"
            id="genus"
            name="genus"
            value={taxonData.genus}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the genus (e.g., Canis, Felis)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="species">
            Species:
          </label>
          <input
            type="text"
            id="species"
            name="species"
            value={taxonData.species}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the species (e.g., lupus, catus)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="subspecies">
            Subspecies (optional):
          </label>
          <input
            type="text"
            id="subspecies"
            name="subspecies"
            value={taxonData.subspecies}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the subspecies (e.g., familiaris)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="commonName">
            Common Name (optional):
          </label>
          <input
            type="text"
            id="commonName"
            name="commonName"
            value={taxonData.commonName}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the common name (e.g., Dog, Cat)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="taxonomicRank">
            Taxonomic Rank:
          </label>
          <input
            type="text"
            id="taxonomicRank"
            name="taxonomicRank"
            value={taxonData.taxonomicRank}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the taxonomic rank (e.g., Species, Subspecies)"
          />
        </div>

        <button
          onClick={generateTaxonomicName}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Generate Taxonomic Name
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="calculator-section mt-12"
          >
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-6 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Taxonomic Name</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Scientific Name</span>
                      <span className="font-medium">{results.taxonomicNameComponents.scientificName}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Common Name</span>
                      <span className="font-medium">{results.taxonomicNameComponents.commonName}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Full Taxonomic Name</span>
                      <span className="font-medium">{results.taxonomicNameComponents.fullTaxonomicName}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Abbreviated Name</span>
                      <span className="font-medium">{results.taxonomicNameComponents.abbreviatedName}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Taxonomic Identity</span>
                      <span className="font-medium">{results.taxonomicNameComponents.taxonomicIdentity}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Taxonomic Rules</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Genus</span>
                      <span className="font-medium">{results.taxonomicRules.genus}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Species</span>
                      <span className="font-medium">{results.taxonomicRules.species}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Subspecies</span>
                      <span className="font-medium">{results.taxonomicRules.subspecies}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Common Name</span>
                      <span className="font-medium">{results.taxonomicRules.commonName}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Rank</span>
                      <span className="font-medium">{results.taxonomicRules.rank}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Example Usage</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Research</span>
                      <span className="font-medium">{results.exampleUsage.research}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Education</span>
                      <span className="font-medium">{results.exampleUsage.education}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Conservation</span>
                      <span className="font-medium">{results.exampleUsage.conservation}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Documentation</span>
                      <span className="font-medium">{results.exampleUsage.documentation}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Component Chart</h4>
                  <div className="h-96">
                    <Bar
                      data={chartData.bar}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Component Level'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Taxonomic Component'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Taxonomic Component Levels'
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold mb-4">Naming Chart</h4>
                  <div className="h-96">
                    <Pie
                      data={chartData.pie}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Taxonomic Components'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Taxonomic Naming Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Taxonomic Naming Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Taxonomic Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the genus (required)</li>
                <li>Input the species (required)</li>
                <li>Optionally add subspecies</li>
                <li>Optionally add common name</li>
                <li>Input taxonomic rank (required)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Generate Taxonomic Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Generate Taxonomic Name" button</li>
                <li>Review the generated scientific name</li>
                <li>Check the taxonomic rules</li>
                <li>Verify the example usage</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the scientific name and common name</li>
                <li>Review the taxonomic rules for each component</li>
                <li>Verify the example usage for different scenarios</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is taxonomic naming?</h3>
              <p className="text-gray-700">
                Taxonomic naming is the formal system of naming organisms according to established rules. It helps in classifying and identifying species consistently across scientific literature.
              </p>
            </div>

            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What components are required?</h3>
              <p className="text-gray-700">
                The minimum required components are:
                <br/><br/>
                - Genus: Capitalized and italicized
                <br/>
                - Species: Lowercase and italicized
                <br/>
                - Taxonomic Rank: Plain text
              </p>
            </div>

            <div className="border-l-4 border-yellow-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">Why is taxonomic naming important?</h3>
              <p className="text-gray-700">
                Taxonomic naming helps in:
                <br/><br/>
                - Standardizing species references
                <br/>
                - Improving scientific communication
                <br/>
                - Preserving biological classification
                <br/>
                - Enhancing biological education
              </p>
            </div>

            <div className="border-l-4 border-purple-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is a subspecies?</h3>
              <p className="text-gray-700">
                A subspecies is a taxonomic rank below species, used to denote populations that are geographically isolated and exhibit distinct characteristics.
              </p>
            </div>

            <div className="border-l-4 border-red-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is a common name?</h3>
              <p className="text-gray-700">
                A common name is a non-scientific, vernacular name for an organism, often used in everyday language.
              </p>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">How do I choose a taxonomic rank?</h3>
              <p className="text-gray-700">
                Common taxonomic ranks include:
                <br/><br/>
                - Kingdom
                <br/>
                - Phylum
                <br/>
                - Class
                <br/>
                - Order
                <br/>
                - Family
                <br/>
                - Genus
                <br/>
                - Species
                <br/>
                - Subspecies
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Generate standardized taxonomic names</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Support for subspecies naming</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Common name integration</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Taxonomic rank specification</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Scientific name validation</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-green-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Consistent scientific references</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Improved biological classification</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Preserved taxonomic hierarchy</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Enhanced scientific communication</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Standardized documentation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Taxonomic Naming <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Taxonomic naming is a systematic approach to creating standardized names for organisms. It involves careful consideration of various components to ensure the names are accurate, consistent, and meaningful.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Genus:</span> Capitalized and italicized
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Species:</span> Lowercase and italicized
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Subspecies:</span> Lowercase and italicized if present
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Common Name:</span> Plain text
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-purple-600 rounded-full mr-2"></span>
              <span className="font-medium">Taxonomic Rank:</span> Plain text
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Taxonomic names are typically used in scientific publications, field guides, and conservation documents to maintain consistent species references.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TaxonomicNamingCalculator;